import { Backbone } from "extensions/backbone";
import { _ } from "extensions/underscore";

import strings from "common/strings";

import { ParameterizedCollection } from "common/collections/parameterizedCollection";

export const EntityNode = Backbone.Model.extend({
    defaults: () => ({
        display_name: "",
        children: []
    }),
    
    initialize() {
        this.setChildren();
    },
    
    setChildren(children = this.get("children")) {
        this.children = new Entities(children);
    
        // Add reference from children back to parent
        this.children.parentEntity = this;
        
        // Remove children prop from attributes
        this.unset("children");
    },
    
    /**
     * Check if current Entity node has sub-entities
     * @returns {boolean}
     */
    hasChildren() {
        return !!this.children.length;
    }
});

export const Entities = ParameterizedCollection.extend({
    model: EntityNode,
    
    comparator(model) {
        const displayName = (model.get("display_name") || "").toLowerCase();
    
        const comparatorMapper = {
            other: "zzzzzz1",
            unidentified: "zzzzzz2",
            regular: displayName
        };
    
        // We want to put any N/A or Unknown all the way to the bottom
        if (displayName === strings.other.toLowerCase()) {
            return comparatorMapper.other;
        }
    
        if (displayName === strings.unidentified.toLowerCase()) {
            return comparatorMapper.unidentified;
        }
    
        return comparatorMapper.regular;
    },
    
    isTopLevel(id) {
        return this.has(id);
    },
    
    /**
     * Get all merchants that the user has access to in a flat list
     * @returns {Array}
     */
    getAll(addPath = false) {
        const getParentPath = function(merchant) {
            const getter = function(m) {
                return m.collection && m.collection.parentEntity ? `${m.collection.parentEntity.get("display_name")}>${getter(m.collection.parentEntity)}` : "";
            };
            
            return _.rest(getter(merchant).split(">").filter((el) => !!el).reverse()).join(" > ");
        };
        
        const getFlatMap = function(merchant) {
            let merchantItself;
            if (addPath && merchant.collection && merchant.collection.parentEntity) {
                merchantItself = [Object.assign(merchant.toJSON(), { parent: getParentPath(merchant) })];
            } else {
                merchantItself = [merchant.toJSON()];
            }
            // eslint-disable-next-line unicorn/prefer-array-flat
            return merchant.children ? merchantItself.concat(_.flatten(merchant.children.map(getFlatMap))) : merchantItself;
        };
        // eslint-disable-next-line unicorn/prefer-array-flat
        return _.flatten(this.map(getFlatMap));
    },

    /**
     * Recursively find a tree for the selected node.
     * Only children and grand+ children.
     * Pass includeParent: true to include the found node as the top object
     * @param childId
     * @returns {*}
     */
    getChildNodeWithItsTree(childId, { includeParent = false } = {}) {
        const getObject = function(list, callback) {
            list.each((item) => {
                if (item.id === childId) {
                    callback(includeParent ? new Entities([item]) : item.children);
                    return;
                }

                if (item.children.length) {
                    getObject(item.children, callback);
                }
            });
        };

        let i;
        getObject(this, (c) => {
            i = c;
        });

        return i;
    },
    
    /**
     * Get only top level (root) items
     * @param {Boolean} [collection=false] should the function return top level items as {Entity} collection
     */
    getTopLevel(collection = false) {
        const topLevel = this.map((merchant) => merchant.omit("children"));
        
        if (collection) {
            return new Entities(topLevel);
        }
    
        return topLevel;
    },
    
    /**
     * Recursively find tree with requested entity node id
     * @param childId
     */
    findNode(childId, collection = true) {
        const match = this.find((model) => model.get("id") === childId || model.children.findNode(childId));
        
        if (match) {
            if (collection) {
                return new Entities([match]);
            }
            return match;
        }
    },
    
    addChildren(parentId, children) {
        const match = this.findNode(parentId);
        if (match && match.get(parentId)) {
            match.get(parentId).setChildren(children);
        } else {
            throw new Error(`Cannot find a parent with id ${parentId}`);
        }
    }
});
